import React, {
  cloneElement,
  isValidElement,
  useCallback,
  useMemo,
  useState,
} from "react";

import {
  useListContext,
  useResourceContext,
  RecordContextProvider,
} from "ra-core";

import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
} from "@material-ui/core";

import { ChevronRight as MuiChevronRight } from "@material-ui/icons";

function AccordionDatagrid(props) {
  const {
    children,
    expand,
    isRowExpandable,
    rowStyle,
    hover,
    rowClick,
    onToggleExpand,
    chevronHeader = null,
    basePath,
    defaultExpandedId = null,
    ...tableProps
  } = props;

  const { ids = [], data = {}, loaded, total } = useListContext(props);
  const resource = useResourceContext(props);

  const [expandedId, setExpandedId] = useState(defaultExpandedId);

  const fields = useMemo(() => React.Children.toArray(children), [children]);
  const columnCount = 1 + fields.length;

  const toggle = useCallback(
    (id, record) => {
      const nextId = expandedId === id ? null : id;
      setExpandedId(nextId);
      if (onToggleExpand) onToggleExpand(nextId);

      if (typeof rowClick === "function") {
        try {
          rowClick(id, record);
        } catch (e) {
          /* ignore */
        }
      }
    },
    [expandedId, onToggleExpand, rowClick]
  );

  const renderHeader = () => (
    <TableHead>
      <TableRow>
        <TableCell>{chevronHeader}</TableCell>

        {fields.map((child, i) => {
          if (!isValidElement(child)) return <TableCell key={i} />;

          const label = child.props.label || child.props.source || null;

          return <TableCell key={i}>{label}</TableCell>;
        })}
      </TableRow>
    </TableHead>
  );

  const renderCell = (child, record, index) => {
    if (!isValidElement(child)) {
      return <TableCell key={index} />;
    }

    const element = cloneElement(child, {
      record,
      resource,
      basePath,
    });

    return <TableCell key={index}>{element}</TableCell>;
  };

  const renderExpand = (id, record) => {
    if (!expand) return null;

    let content = null;

    if (typeof expand === "function") {
      // If expand is a render function
      content = expand({ id, record, resource, basePath });
    } else if (isValidElement(expand)) {
      // If expand is a static React element
      content = cloneElement(expand, {
        id,
        record,
        resource,
        basePath,
      });
    }

    if (!content) return null;

    return (
      <TableRow key={id + "-expand"}>
        <TableCell colSpan={columnCount} style={{ paddingTop: 0 }}>
          <RecordContextProvider value={record}>
            {content}
          </RecordContextProvider>
        </TableCell>
      </TableRow>
    );
  };

  // Loading state
  if (loaded === false) {
    return (
      <Table {...tableProps}>
        {renderHeader()}
        <TableBody>
          <TableRow>
            <TableCell colSpan={columnCount}>Loading…</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    );
  }

  // Empty state
  if (loaded && (ids.length === 0 || total === 0)) {
    return null;
  }

  return (
    <Table {...tableProps}>
      {renderHeader()}

      <TableBody>
        {ids.map((id, rowIndex) => {
          const record = data[id];
          if (!record) return null;

          const expandable = isRowExpandable ? isRowExpandable(record) : true;
          const isOpen = expandedId === id;

          return (
            <React.Fragment key={id}>
              <RecordContextProvider value={record}>
                <TableRow
                  hover={hover}
                  onClick={() => toggle(id, record)}
                  style={rowStyle ? rowStyle(record, rowIndex) : undefined}
                >
                  <TableCell
                    style={{
                      width: 32,
                      maxWidth: 32,
                      paddingLeft: 8,
                      paddingRight: 4,
                    }}
                  >
                    <IconButton
                      aria-label={isOpen ? "Collapse row" : "Expand row"}
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (expandable) toggle(id, record);
                      }}
                      disabled={!expandable}
                      style={{
                        transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
                        transition: "transform 150ms ease",
                      }}
                    >
                      <MuiChevronRight fontSize="small" />
                    </IconButton>
                  </TableCell>

                  {fields.map((child, i) => renderCell(child, record, i))}
                </TableRow>
              </RecordContextProvider>

              {isOpen && renderExpand(id, record)}
            </React.Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
}

export default AccordionDatagrid;
